import React, { useCallback, useEffect } from 'react';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

import { BrandIcon, theme } from '@sg/garnish';

export const OrderPlaybackTimer = (props: OrderPlaybackTimerProps) => {
  const { delay, duration, skipAnimation, iconSrc, iconAlt } = props;

  const animationProgress = useSharedValue(0);
  const containerWidth = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => {
    const scaleX = animationProgress.value;
    return { transform: [{ scaleX }] };
  });

  const iconStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: (containerWidth.value * animationProgress.value) / 2 },
        { translateX: '-50%' },
        { translateY: '-45%' },
      ],
    };
  });

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      containerWidth.value = event.nativeEvent.layout.width;
    },
    [containerWidth],
  );

  useEffect(() => {
    animationProgress.value = skipAnimation
      ? 2
      : withDelay(delay, withTiming(2, { duration, easing: Easing.inOut(Easing.cubic) }));
  }, [animationProgress, delay, duration, skipAnimation]);

  return (
    <View style={styles.container} onLayout={handleLayout}>
      <View style={styles.track}>
        <Animated.View style={[styles.animatedOverlay, animatedStyle]} />
      </View>

      {iconSrc ? (
        <BrandIcon alt={iconAlt} src={iconSrc} style={[styles.brandIcon, iconStyle]} />
      ) : null}
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: 4,
  },
  track: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.CUCUMBER,
    overflow: 'hidden',
  },
  animatedOverlay: {
    position: 'absolute',
    left: '-50%',
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.KALE,
  },
  brandIcon: {
    position: 'absolute',
    zIndex: 6,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderPlaybackTimerProps = {
  delay: number;
  duration: number;
  skipAnimation: boolean;
  iconSrc?: string;
  iconAlt?: string;
};
